import {
  type FocusEvent,
  type KeyboardEvent,
  type MutableRefObject,
  type ReactNode,
  type Ref,
  type RefObject,
  useCallback,
  useState,
} from 'react';
import { Breakpoints } from '@pkgs/components/providers/BreakpointProvider';
import { useBreakpoint } from '@pkgs/components/hooks/useBreakpoint';
import { useAutoCompleteStore } from '@pkgs/components/hooks/stores/useAutoCompleteStore';
import { useSearchStore } from '@pkgs/components/hooks/stores/search/useSearchStore';
import { SearchMode } from '@pkgs/components/hooks/stores/search/slices/searchModeSlice';
import { useAiSearch } from '@pkgs/components/hooks/queries/useAiSearch';
import SearchIcon from '@pkgs/components/assets/icons/small/Search.svg';
import { AiStarGradient } from '@pkgs/components/assets/icons/small/AiStarGradient';
import { type InputTextProps } from '@ev-tech/ui-core-components';
import { Autocomplete } from '@base-ui/react/autocomplete';

import { AutoCompleteInputContent } from './AutoCompleteInputContent';

import styles from './AutoCompleteInput.module.css';

type Props = {
  onClearInput?: () => void;
  label?: ReactNode;
  'aria-label'?: string;
  role?: string;
  isFetching?: boolean;
  name?: string;
  'data-testid'?: string;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  autoFocus?: boolean;
  inputElementRef?: RefObject<HTMLInputElement | null>;
};

export const AutoCompleteInput = ({
  onClearInput,
  isFetching,
  onBlur,
  onFocus,
  onKeyDown,
  name,
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
  inputElementRef,
}: Props) => {
  const { breakpoint = 0 } = useBreakpoint();
  const setIsMobileModalOpen = useAutoCompleteStore((state) => state.setIsMobileModalOpen);
  const isMobileModalOpen = useAutoCompleteStore((state) => state.isMobileModalOpen);
  const searchMode = useSearchStore((state) => state.searchMode);
  const { isPending: isAiSearchFetching } = useAiSearch();

  const handleInputAction = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      if (breakpoint < Breakpoints.TABLET_LANDSCAPE) {
        e.stopPropagation();
        setIsMobileModalOpen(true);
      }
    },
    [breakpoint, setIsMobileModalOpen],
  );

  const [isIdle, setIsIdle] = useState(true);

  const setInputRefs = useCallback(
    (element: HTMLInputElement | null, baseUiRef?: Ref<HTMLInputElement>) => {
      if (inputElementRef) {
        (inputElementRef as MutableRefObject<HTMLInputElement | null>).current = element;
      }
      if (typeof baseUiRef === 'function') {
        baseUiRef(element);
      } else if (baseUiRef) {
        (baseUiRef as MutableRefObject<HTMLInputElement | null>).current = element;
      }
    },
    [inputElementRef],
  );

  const handleFocus = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      handleInputAction(e);
      onFocus?.(e);

      if (!isIdle || breakpoint < Breakpoints.TABLET_LANDSCAPE) return;

      const inputElement = e.currentTarget;
      setTimeout(() => {
        if (document.activeElement !== inputElement) return;

        const length = inputElement.value.length;
        inputElement.setSelectionRange(length, length);
        inputElement.scrollLeft = inputElement.scrollWidth;
        setIsIdle(false);
      }, 0);
    },
    [breakpoint, handleInputAction, isIdle, onFocus],
  );

  const handleBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      setIsIdle(true);
      onBlur?.(e);
    },
    [onBlur],
  );

  return (
    <div className={styles.inputContainer} data-ai-fetching={isAiSearchFetching} data-autocomplete-input-shell>
      <div className={styles.startAdornment}>
        {!isMobileModalOpen && searchMode === SearchMode.AI ? <AiStarGradient /> : <SearchIcon aria-label="search-icon" />}
      </div>
      <Autocomplete.Input
        name={name}
        aria-label={ariaLabel}
        autoFocus={isMobileModalOpen && breakpoint < Breakpoints.TABLET_LANDSCAPE}
        data-testid={dataTestId}
        onKeyDown={onKeyDown}
        render={(inputProps) => {
          const { ref: baseUiRef, ...restInputProps } = inputProps;
          return (
            <AutoCompleteInputContent
              inputProps={restInputProps as InputTextProps}
              isFetching={isFetching}
              setInputRefs={(element) => setInputRefs(element, baseUiRef)}
              handleFocus={handleFocus}
              handleBlur={handleBlur}
              onClearInput={onClearInput}
            />
          );
        }}
      />
    </div>
  );
};
